import {useCallback, useEffect, useMemo, useState} from "react";
import {
    createAdminEntity,
    deleteAdminEntity,
    fetchAdminSingleType,
    fetchAllAdminCollection,
    saveAdminSingleType,
    updateAdminEntity,
} from "../../system/content-type-admin-api";
import {CONTENT_TYPE_UIDS} from "../../system/content-type-registry";

export const TYPE_LABELS = {article: "مقاله", video: "ویدئو", guide: "راهنمای کوتاه"};
export const STATUS_LABELS = {published: "منتشرشده", review: "بازبینی", draft: "پیش‌نویس"};
export const STATUS_SEVERITY = {published: "success", review: "warning", draft: null};
export const SURFACES = [
    {key: "public", title: "هلپ‌سنتر عمومی", description: "در دسته‌بندی و نتایج جست‌وجو"},
    {key: "widget", title: "ویجت داخل محصول", description: "هنگام حضور کاربر در صفحه مرتبط"},
    {key: "ai", title: "پاسخ هوش مصنوعی", description: "به‌عنوان منبع پاسخ‌های آینده"},
];
// App places that can show an "i" hint linking to a help article/video. The key must match the app:
// page titles use "page:<page name>" (the page header of zofino-react renders the hint).
export const APP_ANCHORS = [
    {key: "page:goals", label: "عنوان صفحه اهداف"},
    {key: "page:projects", label: "عنوان صفحه پروژه‌ها"},
    {key: "page:tasks", label: "عنوان صفحه کارها"},
    {key: "page:teams", label: "عنوان صفحه تیم‌ها"},
    {key: "page:customers", label: "عنوان صفحه مشتریان"},
    {key: "page:reports", label: "عنوان صفحه گزارش‌ها"},
    {key: "page:inbox", label: "عنوان صفحه پیام‌ها"},
    {key: "page:events-center", label: "عنوان صفحه مرکز رویداد"},
];
export const DEFAULT_SURFACES = {public: true, widget: true, ai: false};
export const DEFAULT_SETTINGS = {
    publicPopularArticles: true,
    publicNewVideos: true,
    publicQuickStart: true,
    publicUpdatedDate: true,
    widgetPageSuggestions: true,
    widgetVideoInPanel: true,
    widgetLiveSupport: true,
    widgetAdvancedContent: false,
};

const UIDS = CONTENT_TYPE_UIDS;
const DAY = 24 * 60 * 60 * 1000;

export const isVideo = (item) => item?.contentType === "video";
export const tagsOf = (item) => (Array.isArray(item?.tags) ? item.tags : []);
export const digits = (value) => String(value ?? "").replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);
export const newSlug = () => `c-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

/** Video linked to an article (a video points at its article through `relatedSlug`). */
export const videoOfArticle = (contents, article) => contents.find((item) => isVideo(item) && item.relatedSlug === article.slug);
export const articleOfVideo = (contents, video) => contents.find((item) => !isVideo(item) && item.slug === video.relatedSlug);

/** Groups the last 30 days of searches that found little or nothing and are not answered by a content title yet. */
export function buildSearchNeeds(logs, contents) {
    const titles = contents.map((item) => String(item.title || "").trim());
    const groups = new Map();
    logs.forEach((log) => {
        const key = String(log.query || "").trim().toLowerCase();
        if (!key) return;
        const group = groups.get(key) || {query: String(log.query).trim(), count: 0, best: 0};
        group.count += 1;
        group.best = Math.max(group.best, Number(log.resultCount) || 0);
        groups.set(key, group);
    });
    return [...groups.values()]
        .filter((group) => group.best <= 2 && !titles.includes(group.query))
        .map((group) => ({...group, label: group.best === 0 ? "بدون نتیجه" : "نتیجه ضعیف"}))
        .sort((a, b) => b.count - a.count)
        .slice(0, 30);
}

/** Share of published content that is healthy: articles with a video, content with tags and with a summary. */
export function knowledgeHealth(contents) {
    const published = contents.filter((item) => item.contentStatus === "published");
    if (!published.length) return {score: 0, weakest: "هنوز محتوای منتشرشده‌ای وجود ندارد."};
    const articles = published.filter((item) => !isVideo(item));
    const ratio = (list, test) => (list.length ? list.filter(test).length / list.length : 1);
    const parts = [
        {label: "ارتباط مقاله و ویدئو", value: ratio(articles, (a) => Boolean(videoOfArticle(contents, a)))},
        {label: "برچسب‌گذاری محتوا", value: ratio(published, (i) => tagsOf(i).length > 0)},
        {label: "توضیح کوتاه محتوا", value: ratio(published, (i) => Boolean(i.summary))},
    ];
    const score = Math.round((parts.reduce((sum, part) => sum + part.value, 0) / parts.length) * 100);
    const weakest = parts.reduce((min, part) => (part.value < min.value ? part : min), parts[0]);
    return {score, weakest: `بیشترین ضعف فعلی: ${weakest.label}.`};
}

/** Loads every dataset the "مدیریت محتوی" tabs share. */
export function useHelpCms() {
    const [state, setState] = useState({contents: [], categories: [], tags: [], logs: [], settings: DEFAULT_SETTINGS});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const since = new Date(Date.now() - 30 * DAY).toISOString();
            const [contents, categories, tags, logs, settings] = await Promise.all([
                fetchAllAdminCollection(UIDS.HELP_CONTENT, {pageSize: 200, sort: "createdAt:ASC"}),
                fetchAllAdminCollection(UIDS.HELP_CATEGORY, {pageSize: 200, sort: "sortOrder:ASC"}),
                fetchAllAdminCollection(UIDS.HELP_TAG, {pageSize: 200, sort: "sortOrder:ASC"}),
                fetchAllAdminCollection(UIDS.HELP_SEARCH_LOG, {pageSize: 200, maxPages: 10, "filters[createdAt][$gte]": since}),
                fetchAdminSingleType(UIDS.HELP_SETTING).catch(() => null),
            ]);
            setState({
                contents: contents.items,
                categories: categories.items,
                tags: tags.items,
                logs: logs.items,
                settings: {...DEFAULT_SETTINGS, ...(settings || {})},
            });
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    const api = useMemo(() => ({
        createContent: (data) => createAdminEntity(UIDS.HELP_CONTENT, data),
        updateContent: (item, data) => updateAdminEntity(UIDS.HELP_CONTENT, item.documentId, data),
        deleteContent: (item) => deleteAdminEntity(UIDS.HELP_CONTENT, item.documentId),
        createCategory: (data) => createAdminEntity(UIDS.HELP_CATEGORY, data),
        updateCategory: (item, data) => updateAdminEntity(UIDS.HELP_CATEGORY, item.documentId, data),
        createTag: (data) => createAdminEntity(UIDS.HELP_TAG, data),
        updateTag: (item, data) => updateAdminEntity(UIDS.HELP_TAG, item.documentId, data),
        deleteTag: (item) => deleteAdminEntity(UIDS.HELP_TAG, item.documentId),
        saveSettings: (data) => saveAdminSingleType(UIDS.HELP_SETTING, data),
    }), []);

    return {...state, loading, error, reload, api};
}
